import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Sanitize AI output — replace internal platform terms before showing to users
function sanitizeAgentMessage(text) {
  if (!text) return text;
  return text
    // Brand name replacements
    .replace(/OpenClaw'?s?\b/gi, (m) => m.endsWith("'s") || m.endsWith("s") && m[m.length-1]==='s' ? "Oi's" : 'Oi')
    .replace(/\bOpenClaw\b/gi, 'Oi')
    // Internal system/infra terms the agent should never surface
    .replace(/\bgateway token\b/gi, 'connection')
    .replace(/\bOpenClaw gateway\b/gi, 'Oi')
    .replace(/\bOpenClaw's scheduling system\b/gi, "Oi's reminder system")
    .replace(/\bOpenClaw's cron\b/gi, "Oi's scheduler")
    .replace(/\bhttps?:\/\/docs\.openclaw\.ai[^\s)]*/gi, '')
    .replace(/\bpairing required\b/gi, 'configuration needed');
}

// Parse message content for links and file attachments
function parseMessageContent(text) {
  if (!text) return { text: '', links: [], files: [] };
  
  const links = [];
  const files = [];
  
  // Match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[1];
    // Check if it's a file URL (blob storage)
    if (url.includes('blob.vercel-storage.com') || url.includes('vercel.blob')) {
      const filename = url.split('/').pop()?.split('?')[0] || 'file';
      const ext = filename.split('.').pop()?.toLowerCase();
      let type = 'file';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) type = 'image';
      else if (['mp3', 'wav', 'ogg', 'm4a', 'webm'].includes(ext)) type = 'audio';
      else if (['mp4', 'webm', 'mov'].includes(ext)) type = 'video';
      else if (ext === 'pdf') type = 'pdf';
      files.push({ url, filename, type });
    } else {
      links.push(url);
    }
  }
  
  // Remove file URLs from text for cleaner display
  let cleanText = text;
  files.forEach(f => {
    cleanText = cleanText.replace(f.url, '').trim();
  });
  
  return { text: cleanText, links, files };
}

// File attachment component for AI responses
function FileAttachment({ file }) {
  const getIcon = () => {
    switch (file.type) {
      case 'image': return '🖼️';
      case 'audio': return '🎵';
      case 'video': return '🎬';
      case 'pdf': return '📄';
      default: return '📎';
    }
  };
  
  const handleClick = () => {
    window.open(file.url, '_blank');
  };
  
  if (file.type === 'image') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          marginTop: 8,
          borderRadius: 12,
          overflow: 'hidden',
          cursor: 'pointer',
          maxWidth: 200,
        }}
        onClick={handleClick}
      >
        <img
          src={file.url}
          alt={file.filename}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </motion.div>
    );
  }
  
  if (file.type === 'audio') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          marginTop: 8,
          padding: '8px 12px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 20 }}>🎵</span>
        <audio controls src={file.url} style={{ height: 32, flex: 1 }} />
      </motion.div>
    );
  }
  
  // Default file link
  return (
    <motion.a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        padding: '8px 14px',
        background: 'rgba(255,255,255,0.12)',
        borderRadius: 12,
        color: 'rgba(255,255,255,0.95)',
        textDecoration: 'none',
        fontSize: 14,
        border: '1px solid rgba(255,255,255,0.15)',
      }}
      whileHover={{ background: 'rgba(255,255,255,0.18)' }}
    >
      <span style={{ fontSize: 18 }}>{getIcon()}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file.filename}
      </span>
      <span style={{ opacity: 0.5, fontSize: 12 }}>↗</span>
    </motion.a>
  );
}

// Inline link component
function InlineLink({ url, children }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: 'rgba(167,139,250,1)',
        textDecoration: 'underline',
        textDecorationColor: 'rgba(167,139,250,0.4)',
      }}
    >
      {children || url}
    </a>
  );
}

function WordFadeText({ text, onComplete }) {
  const [visibleCount, setVisibleCount] = useState(0);
  const { text: cleanText, links } = parseMessageContent(text);
  const words = cleanText.split(/\s+/).filter(Boolean);

  useEffect(() => {
    setVisibleCount(0);
    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        if (prev >= words.length) {
          clearInterval(interval);
          if (onComplete) onComplete();
          return prev;
        }
        return prev + 1;
      });
    }, 115);
    return () => clearInterval(interval);
  }, [text, words.length, onComplete]);

  // Check if word is a link
  const isLink = (word) => /^https?:\/\//.test(word);

  return (
    <>
      {words.map((word, idx) => {
        const wordIsLink = isLink(word);
        return (
          <motion.span
            key={`${word}-${idx}-${text}`}
            initial={{ opacity: 0, y: 5, filter: 'blur(2px)' }}
            animate={{
              opacity: idx < visibleCount ? 1 : 0,
              y: idx < visibleCount ? 0 : 5,
              filter: idx < visibleCount ? 'blur(0px)' : 'blur(2px)'
            }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{ marginRight: 6, display: 'inline-block' }}
          >
            {wordIsLink ? <InlineLink url={word} /> : word}
          </motion.span>
        );
      })}
    </>
  );
}

function AttachmentPreview({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 8,
    }}>
      {attachments.map((att, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: att.type === 'image' && att.preview ? 0 : '4px 8px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          {att.type === 'image' && att.preview ? (
            <img
              src={att.preview}
              alt={att.name}
              style={{
                width: 48,
                height: 48,
                objectFit: 'cover',
                borderRadius: 6,
              }}
            />
          ) : (
            <>
              <span>
                {att.type === 'pdf' ? '📄' : att.type === 'document' ? '📝' : '📎'}
              </span>
              <span style={{ opacity: 0.9 }}>{att.name}</span>
              <span style={{ opacity: 0.5 }}>({formatSize(att.size)})</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function MessageBubble({ message, isLatest }) {
  const isUser = message.role === 'user';
  const initialY = isUser && isLatest ? 180 : 10;
  const content = isUser ? message.content : sanitizeAgentMessage(message.content);
  const { files } = parseMessageContent(content);

  const bubbleStyle = isUser
    ? {
        background: 'rgba(255,255,255,0.2)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.22)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.14)',
      }
    : {
        background: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(255,255,255,0.2)',
        boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
      };

  return (
    <motion.div
      data-testid={`message-${message.id}`}
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        padding: '4px 14px',
        width: '100%', 
        boxSizing: 'border-box',
      }}
      initial={isUser
        ? { opacity: 0, y: initialY, scale: 0.95 }
        : { opacity: 0, y: 16, x: -16, scale: 0.94 }
      }
      animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
      transition={isUser
        ? { type: 'spring', stiffness: 58, damping: 20, mass: 1.15 }
        : { type: 'spring', stiffness: 150, damping: 20, delay: 0.18 }
      }
    >
      <motion.div
        style={{
          position: 'relative',
          maxWidth: '82%',
          padding: '12px 18px',
          borderRadius: 22,
          borderBottomRightRadius: isUser ? 4 : 22,
          borderBottomLeftRadius: isUser ? 22 : 4,
          color: 'rgba(255,255,255,0.95)',
          fontSize: 16, lineHeight: 1.5, fontWeight: 400,
          wordBreak: 'break-word',
          ...bubbleStyle
        }}
        initial={isUser ? false : { opacity: 0, scale: 0.98, y: 6 }}
        animate={isUser ? false : { opacity: 1, scale: 1, y: 0 }}
        transition={isUser ? undefined : { duration: 0.3, delay: 0.24, ease: 'easeOut' }}
      >
        {isUser ? (
          <>
            {content}
            <AttachmentPreview attachments={message.attachments} />
          </>
        ) : (
          <>
            <WordFadeText text={content} />
            {/* Render file attachments for AI messages */}
            {files.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {files.map((file, idx) => (
                  <FileAttachment key={idx} file={file} />
                ))}
              </div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function MessageList({ messages, isLoading, voiceActive, animationPhase }) {
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const userScrolledRef = useRef(false);
  const prevLenRef = useRef(0);
  const [showHistory, setShowHistory] = useState(false);
  const touchStartYRef = useRef(null);
  const focusActive = ['sending', 'glowing', 'sparkling', 'responding'].includes(animationPhase);
  const getFocusMessages = () => {
    if (messages.length === 0) return [];
    const latest = messages[messages.length - 1];
    const prev = messages[messages.length - 2];
    if (latest?.role === 'assistant' && prev?.role === 'user') {
      return messages.slice(-2);
    }
    return messages.slice(-1);
  };

  const renderedMessages = showHistory
    ? messages
    : (focusActive ? getFocusMessages() : messages.slice(-2));

  const scrollToBottom = (behavior = 'smooth') => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior, block: 'end' });
  };

  useEffect(() => {
    if (messages.length > prevLenRef.current) {
      const latest = messages[messages.length - 1];
      if (!latest) return undefined;
      if (latest.role === 'user') {
        setShowHistory(false);
        userScrolledRef.current = false;
      }
      if ((!userScrolledRef.current || latest.role === 'user') && showHistory) {
        const timer = setTimeout(() => scrollToBottom(latest.role === 'user' ? 'auto' : 'smooth'), 60);
        prevLenRef.current = messages.length;
        return () => clearTimeout(timer);
      }
    }
    prevLenRef.current = messages.length;
    return undefined;
  }, [messages, showHistory]);

  useEffect(() => {
    if (voiceActive) {
      setShowHistory(false);
      userScrolledRef.current = false;
    }
  }, [voiceActive]);

  useEffect(() => {
    if (!focusActive) return undefined;
    setShowHistory(false);
    if (!userScrolledRef.current && scrollRef.current) {
      const timer = setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }, 60);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [focusActive, animationPhase]);

  return (
    <motion.div
      data-testid="message-list"
      ref={scrollRef}
      onWheel={(e) => {
        if (e.deltaY < -6) setShowHistory(true);
      }}
      onTouchStart={(e) => {
        touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
      }}
      onTouchMove={(e) => {
        const startY = touchStartYRef.current;
        if (startY == null) return;
        const currentY = e.touches?.[0]?.clientY ?? startY;
        if (Math.abs(currentY - startY) > 18) {
          setShowHistory(true);
        }
      }}
      onTouchEnd={() => {
        touchStartYRef.current = null;
      }}
      onScroll={() => {
        if (!showHistory) return;
        if (!scrollRef.current) return;
        const el = scrollRef.current;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 36;
        userScrolledRef.current = !nearBottom;
      }}
      style={{
        position: 'absolute',
        top: 88,
        left: 0,
        right: 0,
        bottom: voiceActive ? 220 : 90,
        overflowY: showHistory ? 'auto' : 'hidden',
        overflowX: 'hidden',
        zIndex: 10,
        WebkitOverflowScrolling: 'touch',
        padding: '8px 0',
        scrollbarWidth: 'none',
      }}
      animate={{ y: focusActive ? -14 : 0 }}
      transition={{ duration: 0.42, ease: 'easeOut' }}
    >
      <style>{`div[data-testid="message-list"]::-webkit-scrollbar { display: none; }`}</style>

      <div style={{ minHeight: '58%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AnimatePresence initial={false} mode="popLayout">
          {renderedMessages.map((msg, index) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isLatest={index === renderedMessages.length - 1}
            />
          ))}
        </AnimatePresence>
        <div ref={bottomRef} style={{ height: 2 }} />
      </div>
    </motion.div>
  );
}
